import * as Sentry from '@sentry/angular';
import type { BrowserOptions } from '@sentry/angular';
import { environment } from '../../environments/environment';
import { SanitizeUtil } from '../services/sanitize.util';
import { replayTokenGate, replayUrlScrubIntegration } from './replay-token-gate';

/** What Sentry.init reads from an environment file. */
export interface SentryEnvironment {
	version: string;
	sentry: {
		environment: string;
		dsn: string;
		enableLogs: boolean;
		tracesSampleRate: number;
		replaysSessionSampleRate: number;
		replaysOnErrorSampleRate: number;
		beforeSend?: BrowserOptions['beforeSend'];
	};
}

/**
 * Sentry.init's options. Every hook that can see a URL, a log or a breadcrumb scrubs it first:
 * the reset link's `;token=`, the `/sso-relay/` token and the `/InetUserProfile/` exchange URL
 * reach the page URL, span names and breadcrumbs, and failed requests carry their bodies.
 */
export function buildSentryOptions(env: SentryEnvironment = environment, href: string = window.location.href): BrowserOptions {
	const sentry = env.sentry;
	return {
		dsn: sentry.dsn,
		environment: sentry.environment,
		release: `client-portal-${sentry.environment}@${env.version}`,
		// Setting this option to true will send default PII data to Sentry.
		// For example, automatic IP address collection on events
		sendDefaultPii: true,
		integrations: [
			Sentry.browserTracingIntegration(),
			replayUrlScrubIntegration(),
			...replayTokenGate.integrationsAtLoad(href),
		],
		// Tracing
		tracesSampleRate: sentry.tracesSampleRate,
		// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
		tracePropagationTargets: ['localhost', /^https?:\/\/.*\.cxtsoftware\.net/, /^https?:\/\/.*\.cxtpreview\.net/],
		// Session Replay
		replaysSessionSampleRate: sentry.replaysSessionSampleRate,
		replaysOnErrorSampleRate: sentry.replaysOnErrorSampleRate,
		// Scrubbed first, then the environment's own filter.
		beforeSend: (event, hint) => {
			const redacted = SanitizeUtil.redactSentryEvent(event);
			return redacted && sentry.beforeSend ? sentry.beforeSend(redacted, hint) : redacted;
		},
		// TraceService names routing spans after the raw router URL, token included.
		beforeSendTransaction: (event) => SanitizeUtil.redactSentryEvent(event),
		// Runs before Replay copies a breadcrumb into a recording, so it covers both.
		beforeBreadcrumb: (breadcrumb) => SanitizeUtil.redactBreadcrumb(breadcrumb),
		// Sentry Logs: ErrorService.error() writes structured logs, never error events.
		enableLogs: sentry.enableLogs,
		// The last line of defence for every log, including a direct Sentry.logger call.
		beforeSendLog: (log) => SanitizeUtil.redactSentryLog(log),
		// Add custom tags
		initialScope: {
			tags: {
				component: 'client-portal'
			}
		}
	};
}
